import { useState, useEffect, useCallback } from "react";
import {
  getAdminContacts,
  createAdminContact,
  updateAdminContact,
  updateAdminContactStatus,
} from "../../lib/api";
import { toList } from "../../lib/apiAdminUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const STATUSES = [
  "new",
  "contacted",
  "demo_scheduled",
  "trial",
  "active",
  "paused",
  "not_interested",
];
const STATUS_LABELS = {
  new: "Nuevo",
  contacted: "Contactado",
  demo_scheduled: "Demo agendada",
  trial: "Prueba",
  active: "Activo",
  paused: "Pausado",
  not_interested: "No interesado",
};
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-sky-100 text-sky-800 border-sky-200",
  demo_scheduled: "bg-violet-100 text-violet-800 border-violet-200",
  trial: "bg-amber-100 text-amber-800 border-amber-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paused: "bg-slate-100 text-slate-800 border-slate-200",
  not_interested: "bg-red-100 text-red-800 border-red-200",
};

const emptyContact = {
  full_name: "",
  specialty: "",
  country: "",
  city: "",
  phone: "",
  whatsapp: "",
  email: "",
  source: "",
  status: "new",
  notes: "",
  last_interaction_at: "",
  next_action_at: "",
};

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyContact);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await getAdminContacts(params);
      setContacts(toList(res?.data));
    } catch (err) {
      setContacts([]);
      toast.error("Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const openCreate = () => {
    setForm(emptyContact);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setForm({
      full_name: c.full_name || "",
      specialty: c.specialty || "",
      country: c.country || "",
      city: c.city || "",
      phone: c.phone || "",
      whatsapp: c.whatsapp || "",
      email: c.email || "",
      source: c.source || "",
      status: c.status || "new",
      notes: c.notes || "",
      last_interaction_at: c.last_interaction_at || "",
      next_action_at: c.next_action_at || "",
    });
    setEditingId(c.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.last_interaction_at === "") delete payload.last_interaction_at;
      if (payload.next_action_at === "") delete payload.next_action_at;
      if (editingId) {
        await updateAdminContact(editingId, payload);
        toast.success("Contacto actualizado");
      } else {
        await createAdminContact(payload);
        toast.success("Contacto creado");
      }
      setDialogOpen(false);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateAdminContactStatus(id, status);
      toast.success("Estado actualizado");
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Contactos / Leads</h1>
          <p className="text-slate-500 mt-1">Médicos o instituciones interesados en Aurora Geriátrica</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-800 hover:bg-slate-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo contacto
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <CardTitle className="text-lg">Lista de contactos</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-500">Cargando...</div>
          ) : contacts.length === 0 ? (
            <div className="py-12 text-center text-slate-500">No hay contactos</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Especialidad</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.full_name || "-"}</TableCell>
                    <TableCell>{c.specialty || "-"}</TableCell>
                    <TableCell>{c.country || "-"}</TableCell>
                    <TableCell>{c.email || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLORS[c.status] || ""}>
                        {STATUS_LABELS[c.status] || c.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(c)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {STATUSES.filter((s) => s !== c.status).map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => handleStatusChange(c.id, s)}
                            >
                              Estado → {STATUS_LABELS[s]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar contacto" : "Nuevo contacto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nombre completo</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Dr. Juan Pérez"
                />
              </div>
              <div>
                <Label>Especialidad</Label>
                <Input
                  value={form.specialty}
                  onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                  placeholder="Geriatría"
                />
              </div>
              <div>
                <Label>País</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="Guatemala"
                />
              </div>
              <div>
                <Label>Ciudad</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Origen</Label>
                <Input
                  value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder="landing, referencia, etc."
                />
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Última interacción</Label>
                <Input
                  type="date"
                  value={form.last_interaction_at?.slice(0, 10) || ""}
                  onChange={(e) =>
                    setForm({ ...form, last_interaction_at: e.target.value || null })
                  }
                />
              </div>
              <div>
                <Label>Próxima acción</Label>
                <Input
                  type="date"
                  value={form.next_action_at?.slice(0, 10) || ""}
                  onChange={(e) =>
                    setForm({ ...form, next_action_at: e.target.value || null })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={4}
                placeholder="Notas internas..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-800 hover:bg-slate-700">
                {editingId ? "Guardar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
